import { useState } from "react";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Send, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const ease = [0.16, 1, 0.3, 1];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const valid = form.name.trim() && /\S+@\S+\.\S+/.test(form.email) && form.message.trim().length >= 5;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
      });
      setSent(true);
      setForm({ name: "", email: "", message: "" });
      toast.success("Meldingen er sendt!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt" className="relative py-28 sm:py-36 overflow-hidden" data-testid="contact-section">
      <div className="absolute bottom-0 left-1/4 w-[600px] h-[400px] rounded-full bg-[#FF4800]/[0.04] blur-[140px]" />

      <div className="max-w-3xl mx-auto px-5 sm:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease }}
          className="text-center mb-12"
        >
          <p className="mono text-xs tracking-[0.3em] uppercase text-cyan-400 mb-5">Kontakt</p>
          <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight">
            Spørsmål? <span className="text-cyan-400">Snakk med oss.</span>
          </h2>
          <p className="text-slate-400 mt-5 text-base sm:text-lg">
            Send oss en melding, så svarer vi så raskt vi kan.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.85, delay: 0.1, ease }}
          className="rounded-3xl border border-white/[0.08] bg-[#0F1623] p-7 sm:p-10"
        >
          {sent ? (
            <div className="text-center py-10" data-testid="contact-success">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-cyan-400/10 border border-cyan-400/40">
                <MailCheck size={28} className="text-cyan-400" />
              </div>
              <h3 className="font-display font-bold text-xl mb-2">Meldingen er sendt!</h3>
              <p className="text-slate-400 text-sm mb-7">Vi svarer deg på e-post så raskt vi kan.</p>
              <button onClick={() => setSent(false)} data-testid="contact-new-message-button"
                className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors">
                Send en ny melding
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="c-name">Navn</Label>
                  <Input id="c-name" required value={form.name} onChange={set("name")}
                    data-testid="contact-name-input" className="bg-[#06090E] border-white/10" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="c-email">E-postadresse</Label>
                  <Input id="c-email" type="email" required value={form.email} onChange={set("email")}
                    data-testid="contact-email-input" className="bg-[#06090E] border-white/10" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="c-message">Melding</Label>
                <Textarea id="c-message" required value={form.message} onChange={set("message")}
                  data-testid="contact-message-input" className="bg-[#06090E] border-white/10 min-h-[130px]"
                  placeholder="Hva lurer du på?" />
              </div>
              <button type="submit" disabled={loading || !valid} data-testid="contact-submit-button"
                className="w-full sm:w-auto font-display font-bold bg-[#FF4800] hover:bg-[#ff5d1f] text-white px-10 py-3.5 rounded-full transition-all duration-300 disabled:opacity-40 hover:shadow-[0_0_28px_rgba(255,72,0,0.4)] flex items-center justify-center gap-2">
                {loading ? "Sender..." : <>Send melding <Send size={15} /></>}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
